// Auto-map source headers to target fields, reusing a confirmed mapping when the file's header
// shape has been seen before (from the organization's learned store) or falling back to
// similarity matching.

import { diceCoefficient } from './similarity';
import { emptyMapping, isOrdinaryHeader } from './safeMapping';
import { SELLER_FIELDS, TARGET_FIELD_KEYS } from './targetFields';
import type { ColumnMapping, ParsedFile } from './types';

const MATCH_THRESHOLD = 0.5;

/** Confirmed mappings keyed by file-header signature, as remembered for the organization. */
export type LearnedStore = Record<string, ColumnMapping>;

/** Normalize a header for matching: case- and surrounding-whitespace-insensitive. */
const normalizeHeader = (header: string): string => header.toLowerCase().trim();

/** A stable signature for a file's header set (order-independent, case-insensitive). */
export function headerSignature(headers: string[]): string {
  return headers.map(normalizeHeader).sort().join('|');
}

type SellerField = (typeof SELLER_FIELDS)[number];

/** Whether a header is the field's own label or key, ignoring case and surrounding space. */
export function isExactFieldName(header: string, field: SellerField): boolean {
  const h = normalizeHeader(header);
  return h === field.label.toLowerCase() || h === field.key.toLowerCase();
}

/** Similarity of a header to a field: exact name/key = 1, exact alias = 0.95, else fuzzy (capped
 * just below an exact alias so exact matches always win ties). */
function scoreHeaderForField(header: string, field: SellerField): number {
  if (isExactFieldName(header, field)) return 1;
  const h = normalizeHeader(header);
  if (field.aliases.some((a) => a.toLowerCase() === h)) return 0.95;
  const candidates = [
    field.label.toLowerCase(),
    field.key.toLowerCase(),
    ...field.aliases.map((a) => a.toLowerCase()),
  ];
  const dice = Math.max(...candidates.map((c) => diceCoefficient(h, c)));
  return Math.min(dice, 0.94);
}

/**
 * Build an initial mapping for a file. If we've seen this header shape before, reuse the
 * learned mapping; otherwise fall back to similarity matching. One target field maps to at
 * most one source column (first/highest wins).
 */
export function autoMap(
  file: ParsedFile,
  learned: LearnedStore
): {
  mapping: ColumnMapping;
  fromMemory: boolean;
} {
  const remembered = learned[headerSignature(file.headers)];
  if (remembered) {
    // The signature is case/whitespace-insensitive, so match learned entries the same way — a
    // re-cased header set (e.g. "PRODUCT" vs "Product") still maps instead of returning an empty
    // mapping under the "from memory" banner. Stale stored values (unknown field keys) drop to ''.
    const learnedByNorm = new Map(
      Object.entries(remembered).map(([header, key]) => [
        normalizeHeader(header),
        TARGET_FIELD_KEYS.has(key) ? key : '',
      ])
    );
    const mapping = emptyMapping();
    for (const header of file.headers.filter(isOrdinaryHeader))
      mapping[header] = learnedByNorm.get(normalizeHeader(header)) ?? '';
    return { mapping, fromMemory: true };
  }

  // Score every (header, field) pair and assign greedily by descending score, so each field gets its
  // BEST-matching header (e.g. the literal "Length" column beats "Size" for the length field) rather
  // than whichever header is processed first. One header per field, one field per header.
  const headers = file.headers.filter(isOrdinaryHeader);
  const mapping = blankMappingOf(headers);

  const pairs: { header: string; field: string; score: number }[] = [];
  for (const header of headers) {
    for (const field of SELLER_FIELDS) {
      const score = scoreHeaderForField(header, field);
      if (score >= MATCH_THRESHOLD)
        pairs.push({ header, field: field.key, score });
    }
  }
  pairs.sort((a, b) => b.score - a.score);

  assignWithoutReuse(mapping, pairs);
  return { mapping, fromMemory: false };
}

function blankMappingOf(headers: string[]): ColumnMapping {
  const mapping = emptyMapping();
  for (const header of headers) mapping[header] = '';
  return mapping;
}

/** Maps each pair in order unless its header is already mapped or its field already used. */
function assignWithoutReuse(
  mapping: ColumnMapping,
  pairs: { header: string; field: string }[]
): void {
  const usedFields = new Set(Object.values(mapping).filter(Boolean));
  for (const { header, field } of pairs) {
    if (mapping[header] !== '' || usedFields.has(field)) continue;
    mapping[header] = field;
    usedFields.add(field);
  }
}

export function seededMap(
  file: ParsedFile,
  learned: LearnedStore,
  proposed?: ColumnMapping
): { mapping: ColumnMapping; fromMemory: boolean } {
  const auto = autoMap(file, learned);
  if (auto.fromMemory || !proposed) return auto;

  const headers = file.headers.filter(isOrdinaryHeader);
  const mapping = blankMappingOf(headers);
  const pairsFrom = (source: ColumnMapping) =>
    headers
      .map((header) => ({ header, field: source[header] ?? '' }))
      .filter(({ field }) => TARGET_FIELD_KEYS.has(field));

  assignWithoutReuse(mapping, pairsFrom(proposed));
  assignWithoutReuse(mapping, pairsFrom(auto.mapping));
  return { mapping, fromMemory: false };
}
